// src/pages/AiMenuPage.js
import React, { useState, useEffect } from 'react';
import { useParams, Link,useNavigate } from 'react-router-dom';
import { 
  getRestaurantBySlug, // Keep this for now, might remove later if unused
  addMenuItem 
} from '../services/firebaseService';
import { uploadFile } from '../utils/uploadHelper';
import { getCurrencySymbol } from '../config/currencies'; // Import currency helper
import { doc, getDoc } from 'firebase/firestore'; // Import Firestore functions
import { db } from '../firebase'; // Import db instance
import LoadingSpinner from '../components/loadingspinner';// Adjusted path

function AiMenuPage() {
  const { restaurantId } = useParams(); // Changed from slug to restaurantId
  const [restaurant, setRestaurant] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const navigate = useNavigate();
  
  // Image upload states
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [processingProgress, setProcessingProgress] = useState('');
  
  // Extracted menu items
  const [extractedItems, setExtractedItems] = useState([]);
  const [isAdding, setIsAdding] = useState(false);
  const [selectedItems, setSelectedItems] = useState([]);

  useEffect(() => {
    const fetchRestaurant = async () => {
      try {
        setLoading(true);
        console.log("AiMenuPage: Fetching data for restaurant ID:", restaurantId);

        // Fetch restaurant data using restaurantId
        const restaurantDocRef = doc(db, 'restaurants', restaurantId);
        const restaurantSnap = await getDoc(restaurantDocRef);

        if (!restaurantSnap.exists()) {
          console.error("AiMenuPage: Restaurant not found for ID:", restaurantId);
          setError('Restaurant not found. Please check the URL or go back.');
          setLoading(false);
          return;
        }
        
        const restaurantData = { id: restaurantSnap.id, ...restaurantSnap.data() };
        console.log("AiMenuPage: Restaurant data received:", restaurantData);
        setRestaurant(restaurantData);
        setLoading(false);
      } catch (error) {
        console.error('AiMenuPage: Error fetching restaurant:', error);
        setError('Failed to load restaurant data. Please try again.');
        setLoading(false);
      }
    };
    
    if (restaurantId) { // Fetch only if restaurantId is present
      fetchRestaurant();
    }
  }, [restaurantId]); // Depend on restaurantId

  const handleImageChange = (e) => {
    if (e.target.files[0]) {
      setImageFile(e.target.files[0]);
      setImagePreview(URL.createObjectURL(e.target.files[0]));
      setExtractedItems([]); // Clear previous results
    }
  };

  const removeImage = () => {
    setImageFile(null);
    setImagePreview(null);
    setExtractedItems([]);
  };

  const processImageWithAI = async () => {
    if (!imageFile || !restaurant) return;

    try {
      setIsProcessing(true);
      setProcessingProgress('Uploading image...');
      
      // Upload image to Firebase Storage for OpenAI to access
      const imageUrl = await uploadFile(
        imageFile, 
        `restaurants/${restaurant.id}/ai-menu-analysis`
      );
      
      setProcessingProgress('Processing with AI...');
      
      // Call your Firebase Function to process the image with OpenAI
      // Call your Firebase Function to process the image with OpenAI
      const functionUrl = 'https://analyzemenu-msanpbmmeq-uc.a.run.app/'; // Updated with the deployed function URL
      if (!functionUrl) {
        throw new Error('Please replace YOUR_FIREBASE_FUNCTION_URL_HERE in AiMenuPage.js with your actual analyzeMenu function URL.');
      }
      const response = await fetch(functionUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        },
        body: JSON.stringify({ imageUrl, currency: restaurant.currency }), // Add currency here
        mode: 'cors' 
      });
      
      // Improved error handling: Check response status and log details
      if (!response.ok) {
        let errorBody = 'Could not read error body';
        try {
          errorBody = await response.text(); // Try to get text first
        } catch (textError) {
          console.error('Error reading response text:', textError);
        }
        console.error('Server response:', response.status, response.statusText, errorBody);
        throw new Error(`Failed to process image. Server responded with ${response.status}: ${response.statusText}`);
      }
      
      const data = await response.json();
      setExtractedItems(data.menuItems);
      
      // Select all items by default
      setSelectedItems(data.menuItems.map((_, index) => index));
      
      setProcessingProgress('');
      setIsProcessing(false);
    } catch (error) {
      console.error('Error processing image:', error);
      setError(`Failed to process image: ${error.message}`);
      setIsProcessing(false);
      setProcessingProgress('');
    }
  };

  const toggleItemSelection = (index) => {
    setSelectedItems(prev => {
      if (prev.includes(index)) {
        return prev.filter(i => i !== index);
      } else {
        return [...prev, index];
      }
    });
  };

  const addSelectedItems = async () => {
    if (!restaurant || selectedItems.length === 0) return;

    try {
      setIsAdding(true);
      setError('');
      
      for (const index of selectedItems) {
        const item = extractedItems[index];
        
        // Add item to restaurant's menu
        await addMenuItem(restaurant.id, {
          name: item.name,
          description: item.description,
          price: parseFloat(item.price),
          offerPrice: null,
          prepTime: item.prepTime ? parseInt(item.prepTime) : null,
          category: item.category
        }, null); // No image file for AI-generated items
      }
      
      // Success - redirect to edit page using restaurantId
      navigate(`/edit/${restaurantId}`); // Use navigate and restaurantId
    } catch (error) {
      console.error('Error adding menu items:', error);
      setError(`Failed to add menu items: ${error.message}`);
      setIsAdding(false);
    }
  };

  const removeItem = (index) => {
    setExtractedItems(prev => prev.filter((_, i) => i !== index));
    setSelectedItems(prev => {
      // Update selected items indices
      const newSelected = prev
        .filter(i => i !== index)
        .map(i => i > index ? i - 1 : i);
      return newSelected;
    });
  };

  if (loading) {
      return <LoadingSpinner />;
    }

  // Get currency symbol based on restaurant settings
  const currencySymbol = restaurant ? getCurrencySymbol(restaurant.currency) : '$';

  if (error && !restaurant) {
    return (
      <div className="container centered">
        <p className="error-message">{error}</p>
        <Link to="/" className="btn mt-4">Go Home</Link>
      </div>
    );
  }


  return (
    <div className="container">
        <button 
           type="button"
           onClick={() => navigate(`/edit/${restaurantId}`)} // Use restaurantId
           className="btn" 
           style={{
             textDecoration: 'none',
             display: 'inline-block',
             marginTop: '1rem',
             marginBottom: '1rem',
             marginLeft: '8px',
             fontWeight: 'bold',
             fontSize: '1.2rem'
           }}
         >
            Back
         </button>
      <div className="header">
        <h1 style={{textAlign:'center',width: '100%'}}>AI Menu Creator</h1>
      </div>
      
      {error && (
        <div className="error-message mb-4">
          {error}
        </div>
      )}
      
      <div className="ai-menu-upload">
        <h3>Upload Menu Image</h3>
        <p className="mb-4"style={{marginTop:10}}>Upload a photo of your menu board or paper menu. Our AI will extract all menu items.</p>
        
        {imagePreview ? (
          <div className="image-preview-container">
            <img src={imagePreview} alt="Menu preview" className="preview-img" />
            <button 
              onClick={removeImage} 
              className="btn btn-outline mt-2"
              disabled={isProcessing}
            >
              Remove Image
            </button>
          </div>
        ) : (
          <div className="upload-zone" onClick={() => document.getElementById('menuImageInput').click()}>
            <div className="upload-icon">📸</div>
            <p>Click to upload menu image</p>
            <small>Supports JPEG, PNG, GIF, WebP</small>
            <input
              type="file"
              id="menuImageInput"
              accept="image/*"
              onChange={handleImageChange}
              style={{ display: 'none' }}
            />
          </div>
        )}
        
        {imageFile && !isProcessing && extractedItems.length === 0 && (
          <button 
            onClick={processImageWithAI} 
            className="btn mt-4"
            disabled={isProcessing}
          >
            Process with AI
          </button>
        )}
        
        {isProcessing && (
          <div className="processing-status mt-4">
            <div className="processing-spinner"></div>
            <p>{processingProgress}</p>
          </div>
        )}
      </div>
      
      {extractedItems.length > 0 && (
        <div className="extracted-menu-items mt-4">
          <h3>Extracted Menu Items</h3>
          <p className="mb-4">Review and select items to add to your menu</p>
          
          <div className="menu-items-list">
            {extractedItems.map((item, index) => (
              <div 
                key={index} 
                className={`menu-item-card ${selectedItems.includes(index) ? 'selected' : ''}`}
              >
                <div className="menu-item-checkbox">
                  <input
                    type="checkbox"
                    checked={selectedItems.includes(index)}
                    onChange={() => toggleItemSelection(index)}
                  />
                </div>
                
                <div className="menu-item-content">
                  <h4>{item.name}</h4>
                  <p className="item-description">{item.description}</p>
                  <div className="item-details">
                    <span className="item-price">{currencySymbol}{item.price}</span>
                    {item.prepTime && <span className="item-prep-time">{item.prepTime} min</span>}
                    <span className="item-category">{item.category}</span>
                  </div>
                </div>
                
                <button 
                  onClick={() => removeItem(index)}
                  className="remove-item-btn"
                  title="Remove item"
                >
                  ×
                </button>
              </div>
            ))}
          </div>
          
          <div className="action-buttons mt-4">
            <button 
              onClick={addSelectedItems}
              className="btn"
              disabled={isAdding || selectedItems.length === 0}
            >
              {isAdding ? 'Adding...' : `Add ${selectedItems.length} Items`}
            </button>
            
            <Link to={`/edit/${restaurantId}`} className="btn btn-outline ml-2"> 
              Cancel
            </Link>
          </div>
        </div>
      )}
    </div>
  );
}

export default AiMenuPage;